import React, { useState, useEffect } from "react";
import axios from "axios";
import BASE_URL from "../../../assets/global/url";
import { PaginationControlsInner } from "../../../hooks/customHook/paginationHook/usePaginationInner";
import { useServerPagination } from "../../../hooks/customHook/paginationHook/useServerPagination";

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

const ACTION_STYLES = {
  none: {
    bg: "#f3f4f6",
    color: "#6b7280", // Gray
  },
  created: {
    bg: "#eafaf1",
    color: "#1e9e5a", // Green
  },
  updated: {
    bg: "#fff8e6",
    color: "#b8860b", // Amber
  },
  deleted: {
    bg: "#fdecec",
    color: "#d64545", // Red
  },
  approved: {
    bg: "#e8f7ee",
    color: "#15803d", // Dark Green
  },
  rejected: {
    bg: "#fee2e2",
    color: "#b91c1c", // Dark Red
  },
  added: {
    bg: "#e6f7ff",
    color: "#0284c7", // Sky Blue
  },
  "post-approval update": {
    bg: "#f3e8ff",
    color: "#7c3aed", // Purple
  },
  returned: {
    bg: "#fff4e5",
    color: "#ea580c", // Orange
  },
  default: {
    bg: "#eef0fb",
    color: "#4b3f9e", // Indigo
  },
};

const getActionStyle = (actionType) => {
  const key = (actionType || "").toLowerCase();
  return ACTION_STYLES[key] || ACTION_STYLES.default;
};

const renderMessage = (text = "") => {
  const parts = text.split(/(\bSI-\d+\b|\b[A-Z]{2,}-\d+\b)/g);
  return parts.map((part, i) =>
    /^[A-Z]{2,}-\d+$/.test(part) ? (
      <code
        key={i}
        style={{
          backgroundColor: "#eef0fb",
          color: "#4b3f9e",
          padding: "1px 6px",
          borderRadius: "5px",
          fontSize: "0.78rem",
          fontWeight: 600,
        }}
      >
        {part}
      </code>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
};

// Mirrors parseActionTaken in the backend's auditfieldchanges.js - kept as a
// small local copy since the frontend can't import a Node/CommonJS service
// file. actionTaken is either a plain legacy sentence, or
// JSON.stringify({ message, changes }) when field-level changes were
// recorded. Keep this in sync if the backend shape changes.
const parseActionTaken = (actionTaken) => {
  if (typeof actionTaken !== "string") {
    return { message: actionTaken, changes: [] };
  }

  try {
    const parsed = JSON.parse(actionTaken);
    if (
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      "message" in parsed
    ) {
      return {
        message: parsed.message,
        changes: Array.isArray(parsed.changes) ? parsed.changes : [],
      };
    }
  } catch (e) {
    // Not JSON - plain legacy sentence, fall through below.
  }

  return { message: actionTaken, changes: [] };
};

const ExpandedRow = ({ data }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const paginationExpandedRow = useServerPagination(
    `${BASE_URL}/activity_log/getAuditLogChildData`,
    5,
    { audit_log_mother_id: data.id },
  );

  useEffect(() => {
    if (paginationExpandedRow.data.length > 0) {
      setIsLoading(false);
    }
  }, [paginationExpandedRow.data]);

  if (isLoading) {
    return (
      <div className="p-4 bg-light d-flex align-items-center gap-2">
        <div
          className="spinner-border spinner-border-sm text-primary"
          role="status"
        >
          <span className="visually-hidden">Loading…</span>
        </div>
        <span className="text-muted small">Loading changes…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-light">
        <p className="text-danger small mb-0">
          Failed to load changes. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4" style={{ backgroundColor: "#f7f8fc" }}>
      {paginationExpandedRow.data.length > 0 ? (
        <>
          <div style={{ position: "relative" }}>
            {paginationExpandedRow.data.map((entry, index) => {
              const isLast = index === paginationExpandedRow.data.length - 1;
              const actionStyle = getActionStyle(entry.action_type);
              const { message, changes } = parseActionTaken(entry.actionTaken);

              return (
                <div
                  key={entry.id ?? index}
                  className="d-flex"
                  style={{
                    position: "relative",
                    paddingBottom: isLast ? 0 : "18px",
                  }}
                >
                  {/* Timeline connector */}
                  {!isLast && (
                    <div
                      style={{
                        position: "absolute",
                        left: "17px",
                        top: "58px",
                        bottom: "-2px",
                        width: "2px",
                        backgroundColor: "#e3e6f0",
                      }}
                    />
                  )}

                  {/* Avatar */}
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      backgroundColor: "#4b3f9e",
                      color: "#fff",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      zIndex: 1,
                      marginTop: "22px",
                    }}
                  >
                    {getInitials(entry.masterlist?.fullName)}
                  </div>

                  {/* Column: badge above the card */}
                  <div className="flex-grow-1 ms-3">
                    {entry.action_type && (
                      <span
                        className="d-inline-block mb-1"
                        style={{
                          backgroundColor: actionStyle.bg,
                          color: actionStyle.color,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          padding: "3px 8px",
                          borderRadius: "20px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {entry.action_type}
                      </span>
                    )}

                    {/* Content card */}
                    <div
                      className="px-3 py-2"
                      style={{
                        backgroundColor: "#fff",
                        border: "1px solid #eceef5",
                        borderRadius: "10px",
                        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
                      }}
                    >
                      <p
                        className="mb-1"
                        style={{ fontSize: "0.85rem", color: "#2d2d3a" }}
                      >
                        <span className="fw-semibold">
                          {entry.masterlist?.fullName}
                        </span>{" "}
                        <span className="text-muted">
                          {renderMessage(message)}
                        </span>
                      </p>

                      {changes.length > 0 && (
                        <ul
                          className="mb-1 ps-3"
                          style={{ fontSize: "0.78rem", color: "#4b5563" }}
                        >
                          {changes.map((c, i) => (
                            <li key={i}>
                              Old <span className="fw-semibold">{c.field}</span>
                              : {c.oldValue ?? "—"}
                              {"  →  "}
                              New <span className="fw-semibold">{c.field}</span>
                              : {c.newValue ?? "—"}
                            </li>
                          ))}
                        </ul>
                      )}

                      <span
                        className="text-muted"
                        style={{ fontSize: "0.72rem", letterSpacing: "0.02em" }}
                      >
                        {new Date(entry.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "2-digit",
                          year: "numeric",
                        })}{" "}
                        at{" "}
                        {new Date(entry.createdAt).toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination sits once, below the whole list */}
          <PaginationControlsInner {...paginationExpandedRow} />
        </>
      ) : (
        <div className="d-flex align-items-center gap-2 py-2">
          <i className="bi bi-info-circle text-muted" />
          <p className="fst-italic text-muted mb-0 small">
            No field-level changes recorded for this action.
          </p>
        </div>
      )}
    </div>
  );
};

export default ExpandedRow;
